import React, { useEffect, useMemo, useState } from 'react';
import MarkdownIt from 'markdown-it';
import katex from 'katex';
import { FiMaximize2, FiMinimize2 } from 'react-icons/fi';
import { useColorContext } from '../../../context/colorContext';
import 'katex/dist/katex.min.css';

const md = new MarkdownIt({
	html: true,
	linkify: true,
	breaks: true,
	typographer: true,
});

const toSafeHex = (hex, fallback) => {
	const value = String(hex || '').trim();
	return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback = '#000000') => {
	const safeHex = toSafeHex(hex, fallback);
	const r = parseInt(safeHex.slice(1, 3), 16);
	const g = parseInt(safeHex.slice(3, 5), 16);
	const b = parseInt(safeHex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const escapeHtml = (value = '') => String(value)
	.replace(/&/g, '&amp;')
	.replace(/</g, '&lt;')
	.replace(/>/g, '&gt;')
	.replace(/"/g, '&quot;')
	.replace(/'/g, '&#39;');

const renderLatexToHtml = (latex, displayMode = false) => {
	const safeLatex = String(latex || '').trim();
	const smartTextPlaceholderPattern = /@@SMARTTEXT_(?:IMPORTANCE|HTML)_[A-Z0-9_]+@@/g;
	// Some model outputs inject <mark>/<u>/<span> inside math delimiters.
	// Strip those wrappers before KaTeX parsing so highlighted formulas still render.
	const sanitizedLatex = safeLatex
		.replace(/<\/?(?:mark|u|span)(?:\s+[^>]*)?>/gi, '')
		.replace(smartTextPlaceholderPattern, '')
		.trim();
	if (!safeLatex) {
		return '<span class="smart-text-feature-latex-empty"></span>';
	}

	const katexOptions = {
		displayMode,
		throwOnError: false,
		errorsColor: '#ff6b6b',
		strict: 'ignore',
	};

	try {
		const result = katex.renderToString(sanitizedLatex || safeLatex, katexOptions);
		if (result.includes('katex-error') && /\\tag\s*(?:\{[^}]*\}|\d+)/.test(sanitizedLatex || safeLatex)) {
			const strippedLatex = (sanitizedLatex || safeLatex).replace(/\\tag\s*(?:\{[^}]*\}|\d+)/g, '').trim();
			try {
				return katex.renderToString(strippedLatex || '\\text{ }', katexOptions);
			} catch {
				// fall through to return original result
			}
		}
		return result;
	} catch (error) {
		return `<code class="smart-text-feature-latex-fallback">${escapeHtml(sanitizedLatex || safeLatex)}</code>`;
	}
};

const importancePlaceholderMap = {
	I1: { open: '@@SMARTTEXT_IMPORTANCE_I1_OPEN@@', close: '@@SMARTTEXT_IMPORTANCE_I1_CLOSE@@', className: 'smart-text-feature-importance-1' },
	I2: { open: '@@SMARTTEXT_IMPORTANCE_I2_OPEN@@', close: '@@SMARTTEXT_IMPORTANCE_I2_CLOSE@@', className: 'smart-text-feature-importance-2' },
	I3: { open: '@@SMARTTEXT_IMPORTANCE_I3_OPEN@@', close: '@@SMARTTEXT_IMPORTANCE_I3_CLOSE@@', className: 'smart-text-feature-importance-3' },
	U1: { open: '@@SMARTTEXT_IMPORTANCE_U1_OPEN@@', close: '@@SMARTTEXT_IMPORTANCE_U1_CLOSE@@', className: 'smart-text-feature-underline-1' },
	U2: { open: '@@SMARTTEXT_IMPORTANCE_U2_OPEN@@', close: '@@SMARTTEXT_IMPORTANCE_U2_CLOSE@@', className: 'smart-text-feature-underline-2' },
};

const htmlHighlightPlaceholderMap = {
	MARK: { open: '@@SMARTTEXT_HTML_MARK_OPEN@@', close: '@@SMARTTEXT_HTML_MARK_CLOSE@@', className: 'mark' },
	U: { open: '@@SMARTTEXT_HTML_U_OPEN@@', close: '@@SMARTTEXT_HTML_U_CLOSE@@', className: 'u' },
};

const injectImportancePlaceholders = (markdown = '') => {
	let source = String(markdown || '');
	if (!source) return '';

	// Normalize escaped bracket tags produced by some model outputs.
	source = source
		.replace(/\\\[\s*I1\s*\]/gi, '[I1]')
		.replace(/\\\[\s*\/\s*I1\s*\]/gi, '[/I1]')
		.replace(/\\\[\s*I2\s*\]/gi, '[I2]')
		.replace(/\\\[\s*\/\s*I2\s*\]/gi, '[/I2]')
		.replace(/\\\[\s*I3\s*\]/gi, '[I3]')
		.replace(/\\\[\s*\/\s*I3\s*\]/gi, '[/I3]')
		.replace(/\\\[\s*U1\s*\]/gi, '[U1]')
		.replace(/\\\[\s*\/\s*U1\s*\]/gi, '[/U1]')
		.replace(/\\\[\s*U2\s*\]/gi, '[U2]')
		.replace(/\\\[\s*\/\s*U2\s*\]/gi, '[/U2]');

	const replaceTag = (text, tag) => {
		const placeholder = importancePlaceholderMap[tag];
		if (!placeholder) return text;

		const openRegex = new RegExp(`\\[\\s*${tag}\\s*\\]`, 'gi');
		const closeRegex = new RegExp(`\\[\\s*\/\\s*${tag}\\s*\\]`, 'gi');
		return text
			.replace(openRegex, placeholder.open)
			.replace(closeRegex, placeholder.close);
	};

	source = replaceTag(source, 'I1');
	source = replaceTag(source, 'I2');
	source = replaceTag(source, 'I3');
	source = replaceTag(source, 'U1');
	source = replaceTag(source, 'U2');

	return source;
};

const injectHtmlHighlightPlaceholders = (markdown = '') => {
	let source = String(markdown || '');
	if (!source) return '';

	const replacements = [
		{
			open: /<mark(?:\s+[^>]*)?>/gi,
			close: /<\/mark>/gi,
			placeholder: htmlHighlightPlaceholderMap.MARK,
		},
		{
			open: /<u(?:\s+[^>]*)?>/gi,
			close: /<\/u>/gi,
			placeholder: htmlHighlightPlaceholderMap.U,
		},
	];

	replacements.forEach(({ open, close, placeholder }) => {
		source = source.replace(open, placeholder.open);
		source = source.replace(close, placeholder.close);
	});

	return source;
};

const applyVisualDelimiterMarkup = (markdown = '') => {
	let source = String(markdown || '');
	if (!source) return '';

	const delimiterPattern = /(FONDAMENTAUX|METHODE\s*\/\s*ETAPES|EXEMPLES\s*\/\s*CAS|PIEGES\s*\/\s*CONFUSIONS|FORMULES\s*\/\s*RELATIONS)(\s*:)?/gi;

	return source.replace(delimiterPattern, (_match, label, suffix = '') => {
		const normalized = String(label || '').replace(/\s+/g, ' ').trim().toUpperCase();
		return `<span class="smart-text-feature-delimiter">${normalized}</span>${suffix || ''}`;
	});
};

const normalizeHeadingForComparison = (value = '') => String(value || '')
	.toLowerCase()
	.replace(/[\[\]#*_`~>:\-.,;!?(){}"']/g, ' ')
	.replace(/\s+/g, ' ')
	.trim();

const stripDuplicatedTopHeading = (markdown = '', title = '') => {
	const lines = String(markdown || '').replace(/\r\n/g, '\n').split('\n');
	let firstContentLineIndex = 0;
	while (firstContentLineIndex < lines.length && !lines[firstContentLineIndex].trim()) {
		firstContentLineIndex += 1;
	}

	if (firstContentLineIndex >= lines.length) return String(markdown || '');

	const headingMatch = lines[firstContentLineIndex].trim().match(/^#{1,6}\s+(.+)$/);
	if (!headingMatch) return String(markdown || '');

	const firstHeading = normalizeHeadingForComparison(headingMatch[1]);
	const expectedTitle = normalizeHeadingForComparison(title);
	const isPlaceholderHeading = firstHeading === 'smart text';
	const isDuplicatedTitle = Boolean(expectedTitle) && firstHeading === expectedTitle;

	if (!isPlaceholderHeading && !isDuplicatedTitle) return String(markdown || '');

	lines.splice(firstContentLineIndex, 1);
	while (firstContentLineIndex < lines.length && !lines[firstContentLineIndex].trim()) {
		lines.splice(firstContentLineIndex, 1);
	}

	return lines.join('\n');
};

const wrapBareLatexFragments = (markdown = '') => {
	let source = String(markdown || '');
	if (!source) return '';

	const wrapIfNeeded = (text) => {
		const trimmed = String(text || '').trim();
		if (!trimmed) return text;

		const looksLikeLatex = /\\[a-zA-Z]+|\^|_|\{|\}|&|∞|≤|≥/.test(trimmed)
			&& /[a-zA-Z0-9\\^_{}α-ωΑ-Ω]/.test(trimmed);

		if (!looksLikeLatex) return text;
		if (/^\$\$[\s\S]*\$\$$/.test(trimmed) || /^\\\([\s\S]*\\\)$/.test(trimmed)) {
			return text;
		}

		return `\\(${trimmed}\\)`;
	};

	const tagPattern = /<(mark|u|span)(\s+[^>]*)?>([\s\S]*?)<\/\1>/gi;
	source = source.replace(tagPattern, (match, tag, attrs = '', inner = '') => {
		if (/<(?:mark|u|span)\b/i.test(inner)) return match;
		const wrappedInner = wrapIfNeeded(inner);
		return `<${tag}${attrs || ''}>${wrappedInner}</${tag}>`;
	});

	return source;
};

const injectLatexPlaceholders = (markdown = '') => {
	const source = String(markdown || '');
	if (!source) return { text: '', placeholders: [] };

	const placeholders = [];
	let workingText = source;

	// Display math with escaped dollars: \$\$ ... \$\$
	workingText = workingText.replace(/\\\$\\\$([\s\S]+?)\\\$\\\$/g, (_match, expr) => {
		const token = `@@SMARTTEXT_LATEX_${placeholders.length}@@`;
		placeholders.push({ token, html: renderLatexToHtml(expr, true) });
		return token;
	});

	// Display math with escaped brackets: \[ ... \]
	workingText = workingText.replace(/\\\[([\s\S]+?)\\\]/g, (_match, expr) => {
		const token = `@@SMARTTEXT_LATEX_${placeholders.length}@@`;
		placeholders.push({ token, html: renderLatexToHtml(expr, true) });
		return token;
	});

	workingText = workingText.replace(/\$\$([\s\S]+?)\$\$/g, (_match, expr) => {
		const token = `@@SMARTTEXT_LATEX_${placeholders.length}@@`;
		placeholders.push({ token, html: renderLatexToHtml(expr, true) });
		return token;
	});

	// Inline math with escaped dollars: \$ ... \$
	workingText = workingText.replace(/\\\$([^\n]+?)\\\$/g, (_match, expr) => {
		const token = `@@SMARTTEXT_LATEX_${placeholders.length}@@`;
		placeholders.push({ token, html: renderLatexToHtml(expr, false) });
		return token;
	});

	// Inline math with escaped parentheses: \( ... \)
	workingText = workingText.replace(/\\\(([^\n]+?)\\\)/g, (_match, expr) => {
		const token = `@@SMARTTEXT_LATEX_${placeholders.length}@@`;
		placeholders.push({ token, html: renderLatexToHtml(expr, false) });
		return token;
	});

	// (^|[^$@]) replaces a lookbehind: [^$] prevents matching inside $$...$$,
	// [^@] prevents consuming the trailing '@' of a @@TOKEN@@ placeholder.
	workingText = workingText.replace(/(^|[^$@])\$([^$\n]+?)\$(?!\$)/g, (_match, prefix, expr) => {
		const token = `@@SMARTTEXT_LATEX_${placeholders.length}@@`;
		placeholders.push({ token, html: renderLatexToHtml(expr, false) });
		return prefix + token;
	});

	// Fallback for multi-line bracket blocks:
	// [
	//   e^{x} \geq 1
	// ]
	workingText = workingText.replace(/(^|\n)\[\s*\n([\s\S]*?)\n\s*\](?=\n|$)/g, (_match, prefix, expr) => {
		const token = `@@SMARTTEXT_LATEX_${placeholders.length}@@`;
		placeholders.push({ token, html: renderLatexToHtml(expr, true) });
		return `${prefix}${token}`;
	});

	return { text: workingText, placeholders };
};

const SmartTextFeature = ({
	markdownText = '',
	title = 'Course',
	className,
	style,
	colors = {},
	sizes = {},
	showHeader = true,
	enableFullscreen = true,
}) => {
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [isTransitioning, setIsTransitioning] = useState(false);
	
	// Récupérer les couleurs du context VARK
	const colorCtx = useColorContext();
	const {
		activeAccent,
		textPrimary: ctxTextPrimary,
		textSecondary: ctxTextSecondary,
		textTertiary: ctxTextTertiary,
		bgPrimary: ctxBgPrimary,
		bgSecondary: ctxBgSecondary,
		functionalColors,
	} = colorCtx;

	// Utiliser les couleurs du context, avec fallback sur les props
	const accentColor = toSafeHex(colors.accentColor, activeAccent || '#3B82F6');
	const bgPrimary = toSafeHex(colors.bgPrimary, ctxBgPrimary || '#101114');
	const bgSecondary = toSafeHex(colors.bgSecondary, ctxBgSecondary || '#161A21');
	const bgTertiary = toSafeHex(colors.bgTertiary, '#1E1E1E');
	const textPrimary = toSafeHex(colors.textPrimary, ctxTextPrimary || '#F2F5FA');
	const textSecondary = toSafeHex(colors.textSecondary, ctxTextSecondary || '#A8B4C8');
	const textTertiary = toSafeHex(colors.textTertiary, ctxTextTertiary || '#9E9E9E');
	const borderColor = toSafeHex(colors.borderColor, '#2A3444');
	
	// Couleurs pour les alertes
	const errorColor = functionalColors?.error || '#EF4444';
	const infoColor = functionalColors?.info || '#3B82F6';
	const panelBackground = hexToRgba(bgSecondary, 0.96, bgSecondary);
	const blockBackground = hexToRgba(accentColor, 0.1, bgSecondary);
	const panelShadow = `0 12px 34px ${hexToRgba('#000000', 0.19)}`;
	const panelInset = `0 0 0 1px ${hexToRgba(accentColor, 0.14, accentColor)} inset`;

	const width = sizes.width || '100%';
	const height = sizes.height || '100%';
	const minHeight = Number.isFinite(Number(sizes.minHeight)) ? Number(sizes.minHeight) : 320;
	const maxHeight = Number.isFinite(Number(sizes.maxHeight)) ? Number(sizes.maxHeight) : undefined;
	const borderRadius = Number.isFinite(Number(sizes.borderRadius)) ? Number(sizes.borderRadius) : 28;
	const contentPadding = sizes.contentPadding || '24px 24px 28px';

	const renderedMarkdown = useMemo(() => {
		const safeSource = stripDuplicatedTopHeading(String(markdownText || '').trim(), title).trim();
		if (!safeSource) {
			return '<p><em>No markdown content available.</em></p>';
		}

		// Protect inline code spans so LaTeX extraction never runs inside backtick content.
		// Tokens are restored just before md.render() so markdown-it handles them normally.
		const codeSpans = [];
		const codeProtected = safeSource.replace(/`[^`\n]+`/g, (match) => {
			const token = `@@SMARTTEXT_CODE_${codeSpans.length}@@`;
			codeSpans.push(match);
			return token;
		});

		const importanceAwareSource = injectImportancePlaceholders(codeProtected);
		// wrapBareLatexFragments must run BEFORE injectHtmlHighlightPlaceholders so it can
		// still see the original <mark>/<u> HTML tags and wrap bare LaTeX inside them.
		const latexWrappedSource = wrapBareLatexFragments(importanceAwareSource);
		const htmlHighlightAwareSource = injectHtmlHighlightPlaceholders(latexWrappedSource);
		const delimiterAwareSource = applyVisualDelimiterMarkup(htmlHighlightAwareSource);
		const { text, placeholders } = injectLatexPlaceholders(delimiterAwareSource);

		// Restore code spans before markdown rendering so md.render() produces proper <code> tags.
		let textForRender = text;
		codeSpans.forEach((original, i) => {
			textForRender = textForRender.replaceAll(`@@SMARTTEXT_CODE_${i}@@`, original);
		});

		let rendered = md.render(textForRender);
		placeholders.forEach(({ token, html }) => {
			rendered = rendered.replaceAll(token, html);
		});
		rendered = rendered
			.replaceAll(htmlHighlightPlaceholderMap.MARK.open, '<mark>')
			.replaceAll(htmlHighlightPlaceholderMap.MARK.close, '</mark>')
			.replaceAll(htmlHighlightPlaceholderMap.U.open, '<u>')
			.replaceAll(htmlHighlightPlaceholderMap.U.close, '</u>');
		Object.values(importancePlaceholderMap).forEach(({ open, close, className }) => {
			rendered = rendered.replaceAll(open, `<span class="${className}">`);
			rendered = rendered.replaceAll(close, '</span>');
		});
		return rendered;
	}, [markdownText, title]);

	const scrollClassName = 'smart-text-feature-scroll';

	const handleToggleFullscreen = () => {
		setIsTransitioning(true);
		setIsFullscreen((prev) => !prev);
	};

	useEffect(() => {
		if (!isTransitioning) return undefined;
		const timer = window.setTimeout(() => setIsTransitioning(false), 280);
		return () => window.clearTimeout(timer);
	}, [isTransitioning]);

	useEffect(() => {
		if (!isFullscreen) return undefined;

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		const onKeyDown = (event) => {
			if (event.key === 'Escape') setIsFullscreen(false);
		};

		window.addEventListener('keydown', onKeyDown);
		return () => {
			document.body.style.overflow = previousOverflow;
			window.removeEventListener('keydown', onKeyDown);
		};
	}, [isFullscreen]);

	return (
		<>
			{enableFullscreen && (
				<div
					aria-hidden="true"
					onClick={() => setIsFullscreen(false)}
					style={{
						position: 'fixed',
						inset: 0,
						zIndex: 1100,
						background: hexToRgba('#05070C', 0.72, '#05070C'),
						opacity: isFullscreen ? 1 : 0,
						pointerEvents: isFullscreen ? 'auto' : 'none',
						transition: 'opacity 220ms ease',
					}}
				/>
			)}
		<div
			className={className}
			style={{
				width: isFullscreen ? 'min(96vw, 1520px)' : width,
				height: isFullscreen ? '94dvh' : height,
				minHeight: isFullscreen ? 420 : minHeight,
				maxHeight: isFullscreen ? '94dvh' : maxHeight,
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden',
				position: isFullscreen ? 'fixed' : 'relative',
				inset: isFullscreen ? '3dvh auto auto 50%' : 'auto',
				transform: isFullscreen
					? `translateX(-50%) scale(${isTransitioning ? 0.985 : 1})`
					: `scale(${isTransitioning ? 0.995 : 1})`,
				opacity: isTransitioning ? 0.98 : 1,
				zIndex: isFullscreen ? 1200 : 1,
				borderRadius,
				border: `1px solid ${hexToRgba(accentColor, 0.24, accentColor)}`,
				background: panelBackground,
				boxShadow: `${panelShadow}, ${panelInset}`,
				transition: 'transform 240ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease, box-shadow 220ms ease',
				backdropFilter: 'none',
				WebkitBackdropFilter: 'none',
				...style,
			}}
		>
			<style>
				{`
					.${scrollClassName} {
						scrollbar-gutter: stable both-edges;
						scrollbar-width: thin;
						scrollbar-color: ${hexToRgba(accentColor, 0.62, accentColor)} transparent;
					}

					.smart-text-feature-fullscreen-btn:hover {
						transform: translateY(-1px) scale(1.04);
						box-shadow: 0 10px 24px ${hexToRgba(accentColor, 0.34, accentColor)}, 0 0 0 1px ${hexToRgba(textPrimary, 0.2, textPrimary)} inset;
					}

					.smart-text-feature-fullscreen-btn:active {
						transform: translateY(0) scale(0.94);
					}

					.smart-text-feature-fullscreen-btn.is-fullscreen {
						background: ${hexToRgba(accentColor, 0.26, accentColor)};
					}

					.${scrollClassName}::-webkit-scrollbar {
						width: 14px;
					}

					.${scrollClassName}::-webkit-scrollbar-track {
						background: transparent;
					}

					.${scrollClassName}::-webkit-scrollbar-thumb {
						background: ${hexToRgba(accentColor, 0.62, accentColor)};
						border-radius: 999px;
						box-shadow:
							0 4px 14px ${hexToRgba(accentColor, 0.26, accentColor)},
							0 0 0 1px ${hexToRgba(textPrimary, 0.18, textPrimary)} inset;
					}

					.${scrollClassName}::-webkit-scrollbar-thumb:hover {
						background: ${hexToRgba(accentColor, 0.9, accentColor)};
						box-shadow:
							0 6px 18px ${hexToRgba(accentColor, 0.34, accentColor)},
							0 0 0 1px ${hexToRgba(textPrimary, 0.24, textPrimary)} inset;
					}

					.smart-text-feature-markdown {
						font-size: 1.12rem;
						line-height: 1.66;
						letter-spacing: 0.002em;
						color: ${textPrimary};
					}

					.smart-text-feature-markdown h1,
					.smart-text-feature-markdown h2,
					.smart-text-feature-markdown h3,
					.smart-text-feature-markdown h4 {
						margin: 1.1em 0 0.45em;
						line-height: 1.28;
						letter-spacing: 0.01em;
						color: ${hexToRgba(textPrimary, 0.98, textPrimary)};
					}

					.smart-text-feature-markdown h2 {
						font-size: 1.36rem;
						font-weight: 760;
						padding-bottom: 0.2em;
						border-bottom: 1px solid ${hexToRgba(textPrimary, 0.14, textPrimary)};
					}

					.smart-text-feature-markdown h3 {
						font-size: 1.18rem;
						font-weight: 680;
					}

					.smart-text-feature-markdown p,
					.smart-text-feature-markdown li,
					.smart-text-feature-markdown blockquote {
						color: ${hexToRgba(textPrimary, 0.94, textPrimary)};
					}

					.smart-text-feature-markdown ul,
					.smart-text-feature-markdown ol {
						padding-left: 1.22rem;
					}

					.smart-text-feature-markdown li {
						margin: 0.34em 0;
					}

					.smart-text-feature-markdown .smart-text-feature-delimiter {
						display: inline;
						margin: 0;
						padding: 0;
						font-size: 1.02em;
						font-weight: 820;
						letter-spacing: 0.065em;
						text-transform: uppercase;
						color: ${hexToRgba(accentColor, 0.96, accentColor)};
						text-shadow: 0 1px 8px ${hexToRgba(accentColor, 0.2, accentColor)};
					}

					.smart-text-feature-markdown mark {
						background: ${hexToRgba(accentColor, 0.22, accentColor)};
						color: ${hexToRgba(textPrimary, 0.99, textPrimary)};
						padding: 0.06em 0.3em;
						border-radius: 6px;
						box-shadow: 0 0 0 1px ${hexToRgba(accentColor, 0.22, accentColor)} inset;
					}

					.smart-text-feature-markdown .smart-text-feature-importance-1 {
					background: ${hexToRgba(accentColor, 0.96, accentColor)};
					color: #ffffff;
					padding: 0.12em 0.38em;
					border-radius: 8px;
					box-shadow: 0 0 0 2px ${hexToRgba(accentColor, 0.7, accentColor)} inset, 0 4px 18px ${hexToRgba(accentColor, 0.45, accentColor)};
					font-weight: 800;
					letter-spacing: 0.006em;
				}

				.smart-text-feature-markdown .smart-text-feature-importance-2 {
						background: ${hexToRgba(accentColor, 0.56, accentColor)};
					color: ${textSecondary};
					padding: 0.1em 0.36em;
					border-radius: 7px;
					box-shadow: 0 0 0 1.5px ${hexToRgba(accentColor, 0.5, accentColor)} inset, 0 3px 14px ${hexToRgba(accentColor, 0.25, accentColor)};
					font-weight: 750;
					letter-spacing: 0.004em;
				}

				.smart-text-feature-markdown .smart-text-feature-importance-3 {
						background: ${hexToRgba(accentColor, 0.3, accentColor)};
					color: ${textTertiary};
					padding: 0.08em 0.32em;
					border-radius: 7px;
					box-shadow: 0 0 0 1px ${hexToRgba(accentColor, 0.4, accentColor)} inset;
					font-weight: 620;
				}

				.smart-text-feature-markdown .smart-text-feature-underline-1 {
					text-decoration: underline;
					text-decoration-thickness: 0.18em;
					text-decoration-color: ${hexToRgba(errorColor, 0.95, errorColor)};
					text-underline-offset: 0.16em;
					font-weight: 720;
					color: ${errorColor};
				}

				.smart-text-feature-markdown .smart-text-feature-underline-2 {
					text-decoration: underline;
					text-decoration-thickness: 0.12em;
					text-decoration-color: ${hexToRgba(infoColor, 0.88, infoColor)};
					text-underline-offset: 0.14em;
					font-weight: 600;
				}

				.smart-text-feature-markdown blockquote {
					margin: 1em 0;
					padding: 0.62em 0.9em;
					border-left: 3px solid ${hexToRgba(accentColor, 0.72, accentColor)};
						background: ${blockBackground};
					border-radius: 10px;
				}

				.smart-text-feature-markdown blockquote:has(+ ul),
				.smart-text-feature-markdown blockquote:has(+ ol) {
					margin-bottom: 0;
					padding-bottom: 0.28em;
					border-radius: 10px 10px 0 0;
				}

				.smart-text-feature-markdown blockquote + ul,
				.smart-text-feature-markdown blockquote + ol {
					margin: 0 0 1em;
					padding: 0.15em 0.9em 0.72em 2.05em;
					border-left: 3px solid ${hexToRgba(accentColor, 0.72, accentColor)};
						background: ${blockBackground};
					border-radius: 0 0 10px 10px;
				}

				.smart-text-feature-markdown blockquote + ul li,
				.smart-text-feature-markdown blockquote + ol li {
					color: ${hexToRgba(textPrimary, 0.95, textPrimary)};
					margin: 0.22em 0;
				}

				.smart-text-feature-markdown code {
					font-family: 'JetBrains Mono', 'Fira Code', monospace;
					font-size: 0.92em;
					padding: 0.12em 0.36em;
					border-radius: 6px;
					background: ${hexToRgba(textPrimary, 0.08, textPrimary)};
				}

				.smart-text-feature-markdown .katex {
					font-size: 1.12em;
					color: ${textPrimary};
					text-shadow: 0 1px 10px ${hexToRgba(accentColor, 0.18, accentColor)};
				}

				.smart-text-feature-markdown .katex-display {
					margin: 1em 0;
					overflow-x: auto;
					overflow-y: hidden;
					padding: 0.5em 0.4em;
					font-size: 1.26em;
					border-radius: 10px;
						background: ${blockBackground};
					box-shadow:
							0 0 0 1px ${hexToRgba(textPrimary, 0.08, textPrimary)} inset,
							0 8px 22px ${hexToRgba('#000000', 0.2)};
				}

				.smart-text-feature-markdown .smart-text-feature-latex-fallback {
					padding: 0.12em 0.32em;
					border-radius: 6px;
						background: ${hexToRgba(textPrimary, 0.08, textPrimary)};
					}
				`}
			</style>

			{showHeader && (
				<div
					style={{
						padding: '16px 20px 14px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						borderBottom: `1px solid ${hexToRgba(textPrimary, 0.1, textPrimary)}`,
						background: hexToRgba(textPrimary, 0.03, textPrimary),
					}}
				>
					<div
						style={{
							fontSize: '0.92rem',
							fontWeight: 760,
							letterSpacing: '0.04em',
							textTransform: 'uppercase',
							color: hexToRgba(textSecondary, 0.96, textSecondary),
						}}
					>
						{title}
					</div>
					{enableFullscreen && (
						<button
							className={`smart-text-feature-fullscreen-btn ${isFullscreen ? 'is-fullscreen' : ''}`}
							type="button"
							onClick={handleToggleFullscreen}
							aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
							style={{
								width: 34,
								height: 34,
								display: 'grid',
								placeItems: 'center',
								borderRadius: 10,
								border: `1px solid ${hexToRgba(accentColor, 0.35, accentColor)}`,
								background: hexToRgba(accentColor, 0.18, accentColor),
								color: hexToRgba(textPrimary, 0.96, textPrimary),
								cursor: 'pointer',
								boxShadow: `0 6px 18px ${hexToRgba(accentColor, 0.24, accentColor)}, 0 0 0 1px ${hexToRgba(textPrimary, 0.14, textPrimary)} inset`,
								transition: 'transform 180ms ease, box-shadow 200ms ease, background 200ms ease',
							}}
						>
							<span
								style={{
									display: 'grid',
									placeItems: 'center',
									transform: isTransitioning ? 'scale(0.86) rotate(8deg)' : (isFullscreen ? 'rotate(180deg)' : 'rotate(0deg)'),
									transition: 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)',
								}}
							>
								{isFullscreen ? <FiMinimize2 size={16} /> : <FiMaximize2 size={16} />}
							</span>
						</button>
					)}
				</div>
			)}

			<div
				className={scrollClassName}
				style={{
					flex: 1,
					overflowY: 'auto',
					overflowX: 'hidden',
					padding: contentPadding,
				}}
			>
				<div
					className="smart-text-feature-markdown"
					dangerouslySetInnerHTML={{ __html: renderedMarkdown }}
				/>
			</div>
		</div>
		</>
	);
};

export default SmartTextFeature;
